import React, { useCallback, memo } from "react"
import { Box, Typography, FormLabel, FormControl, Select, MenuItem, Skeleton } from "@mui/material"
import { CustomInputField } from "./CustomInputField"
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { ReactComponent as FileUploader } from "../../../../assets/images/KnowledgeBase/file-uploader.svg";

export const BrandingSection = memo(({ settings, onSettingChange, onLogoUpload, loading }) => {
    const fontFamilies = ["Inter", "Roboto", "Lato", "Open Sans", "Poppins", "Montserrat"]

    const handleBusinessNameChange = useCallback((value) => {
        onSettingChange("business_name", value)
    }, [onSettingChange]);

    const handleBrandColorChange = useCallback((value) => {
        onSettingChange("brand_color", value)
    }, [onSettingChange]);

    const handleAccentColorChange = useCallback((value) => {
        onSettingChange("accent_color", value)
    }, [onSettingChange]);

    const handleFontFamilyChange = useCallback((e) => {
        onSettingChange("font_family", e.target.value)
    }, [onSettingChange]);

    return (
        <Box sx={{ mb: '24px' }}>
            <Typography
                variant="subtitle2"
                sx={{
                    fontWeight: 500,
                    color: "#061522",
                    mb: '18px',
                    fontSize: 14,
                    fontFamily: "Inter",
                }}
            >
                Branding
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "flex-start" }}>
                {loading ? (
                    <Skeleton variant="rectangular" width={56} height={56} sx={{ borderRadius: "8px" }} />
                ) : (
                    <Box
                        component="label"
                        sx={{
                            width: 56,
                            height: 56,
                            border: "1px dashed #E5E9EC",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            backgroundColor: "#F9FAFA",
                            transition: "all 0.2s",
                            flexShrink: 0,
                            overflow: "hidden",
                            "&:hover": {
                                backgroundColor: "#F0F4F8",
                                borderColor: settings.brand_color,
                            },
                        }}
                    >
                        {settings.logoPreview ? (
                            <Box
                                component="img"
                                src={settings.logoPreview}
                                alt="Uploaded logo"
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                }}
                            />
                        ) : (
                            <FileUploader />

                        )}
                        <input type="file" accept="image/*" onChange={onLogoUpload} style={{ display: "none" }} />
                    </Box>
                )}

                <Box sx={{ flex: 1 }}>
                    {loading ? (
                        <>
                            <Skeleton variant="text" width="60%" height={24} sx={{ mb: 0.5 }} />
                            <Skeleton variant="text" width="90%" height={20} />
                        </>
                    ) : (
                        <>
                            <Typography
                                sx={{
                                    color: "#061522",
                                    fontFamily: "Inter",
                                    fontSize: 14,
                                    fontWeight: 500,
                                    lineHeight: "20px",
                                    mb: 0.5,
                                }}
                            >
                                Upload Logo
                            </Typography>
                            <Typography
                                sx={{
                                    color: "#556575",
                                    fontFamily: "Inter",
                                    fontSize: 12,
                                    fontWeight: 400,
                                    lineHeight: "20px",
                                }}
                            >
                                Max. 1500×1000 px jpg, jpeg, png, gif
                            </Typography>
                        </>
                    )}
                </Box>
            </Box>

            <Box sx={{ mb: '28px' }}>
                <FormLabel
                    sx={{
                        fontWeight: 400,
                        color: "#405261",
                        fontSize: 12,
                        mb: '5px',
                        display: "block",
                        fontFamily: "Inter",
                    }}
                >
                    Business Name
                </FormLabel>
                {loading ? (
                    <Skeleton variant="rectangular" width={296} height={40} sx={{ borderRadius: "8px" }} />
                ) : (
                    <CustomInputField
                        value={settings.business_name}
                        onChange={handleBusinessNameChange}
                    />
                )}
            </Box>

            <Box sx={{ mb: '28px' }}>
                <FormLabel
                    sx={{
                        fontWeight: 400,
                        color: "#405261",
                        fontSize: 12,
                        mb: '5px',
                        display: "block",
                        fontFamily: "Inter",
                    }}
                >
                    Brand Color
                </FormLabel>
                {loading ? (
                    <Skeleton variant="rectangular" width={296} height={40} sx={{ borderRadius: "8px" }} />
                ) : (
                    <CustomInputField
                        value={settings.brand_color || "#148DFD"}
                        onChange={handleBrandColorChange}
                        placeholder="Color code"
                        isColorInput={true}
                        colorValue={settings.brand_color || "#148DFD"}
                    />
                )}
            </Box>

            <Box sx={{ mb: '28px' }}>
                <FormLabel
                    sx={{
                        fontWeight: 400,
                        color: "#405261",
                        fontSize: 12,
                        mb: '5px',
                        display: "block",
                        fontFamily: "Inter",
                    }}
                >
                    Accent Color
                </FormLabel>
                {loading ? (
                    <Skeleton variant="rectangular" width={296} height={40} sx={{ borderRadius: "8px" }} />
                ) : (
                    <CustomInputField
                        value={settings.accent_color || "#556575"}
                        onChange={handleAccentColorChange}
                        placeholder="Color code"
                        isColorInput={true}
                        colorValue={settings.accent_color || "#556575"}
                    />
                )}
            </Box>

            <Box sx={{ mb: '24px' }}>
                <FormLabel
                    sx={{
                        fontWeight: 400,
                        color: "#405261",
                        fontSize: 12,
                        mb: '5px',
                        display: "block",
                        fontFamily: "Inter",
                    }}
                >
                    Font Family
                </FormLabel>
                {loading ? (
                    <Skeleton variant="rectangular" width={296} height={40} sx={{ borderRadius: "8px" }} />
                ) : (
                    <FormControl variant="standard" sx={{ width: 296 }}>
                        <Select
                            value={settings.font_family || "Inter"}
                            onChange={handleFontFamilyChange}
                            IconComponent={Dropdown}
                            disableUnderline
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        border: "1px solid #D5DCE1",
                                        marginTop: "4px",
                                        padding: ' 0px 8px',
                                        borderRadius: "8px",
                                        "& .MuiMenuItem-root": {
                                            fontSize: "14px",
                                            padding: "8px 8px",
                                            color: "#405261",
                                            "&:hover": {
                                                color: '#061522',
                                                borderRadius: "8px",
                                                background: "#F9FAFA",
                                            },
                                            "&.Mui-selected": {
                                                color: '#061522',
                                                borderRadius: "8px",
                                                background: "#F9FAFA",
                                            },
                                            "&.Mui-selected:hover": {
                                                background: "#F9FAFA",
                                            },
                                        },
                                    },
                                },
                            }}
                            sx={{
                                height: 40,
                                borderRadius: "8px",
                                border: "1px solid #D5DCE1",
                                backgroundColor: "#FFF",
                                boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                                px: '16px',
                                fontFamily: "Inter",
                                fontSize: 14,
                                color: "#556575",
                                "& .MuiSelect-icon": {
                                    right: "16px !important"
                                },
                                "& .MuiMenuItem-root": {
                                    fontFamily: "Inter",
                                    fontSize: 14,
                                    color: "#556575",
                                }
                            }}
                        >
                            {fontFamilies.map((font) => (
                                <MenuItem key={font} value={font}>
                                    {font}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                {/* seprator line */}
                <Box sx={{
                    width: 296,
                    height: '1px',
                    backgroundColor: '#E5E9EC',
                    marginTop: '24px'

                }} />


            </Box>
        </Box>
    )
})
